/* DealScenarioPanel.jsx — Phase 28 per-deal scenario forecasts.
 *
 * Mounted on the Deal Room intelligence rail. Surfaces:
 *  • Current collapse probability with contributing signals
 *  • 3 named forward scenarios (silence, mortgage expiry, weakest link recovers)
 *  • Intervene-now vs delay comparison (when a pending action exists)
 *  • Confidence trajectory (no-intervention vs with-intervention)
 *  • Operational timeline movement
 *
 * Auto-hides when scenarios.active=false.
 */
import React, { useCallback, useEffect, useState } from "react";
import { LineChart, AlertTriangle, Sparkles, Hourglass } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 120_000;

const BAND_TONE = {
  Stable:   { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  Watching: { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  Elevated: { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "At Risk":{ bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
  Critical: { bg: "rgba(244,63,94,0.16)",  fg: "#9F1239" },
};

const fmtGBP = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}K`;
  return `£${Math.round(v).toLocaleString()}`;
};

const SignalRow = ({ signal }) => (
  <li className="flex items-start gap-2 py-1"
      data-testid={`scenario-signal-${signal.signal.replace(/_/g, "-")}`}>
    <span className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
          style={{ background: "#F59E0B" }} />
    <div className="min-w-0">
      <p className="text-[11.5px] text-slate-800 leading-snug">
        <strong className="text-slate-900">+{signal.weight_pts} pts</strong>{" · "}
        {signal.narrative}
      </p>
    </div>
  </li>
);

const ScenarioCard = ({ s }) => {
  const tone = BAND_TONE[s.projection?.band] || BAND_TONE.Watching;
  return (
    <li className="rounded-md p-2.5 border"
        style={{ borderColor: "var(--gp-card-border)" }}
        data-testid={`scenario-${s.kind}`}>
      <p className="text-[11.5px] font-semibold text-slate-900 leading-snug">
        {s.title}
      </p>
      <p className="text-[10.5px] text-slate-500 mt-0.5">
        {s.narrative}
      </p>
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
              style={{ background: tone.bg, color: tone.fg }}>
          {s.projection?.band}
        </span>
        <span className="text-[10.5px] tabular-nums px-2 py-0.5 rounded-md"
              style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
          {s.projection?.score}/100
        </span>
      </div>
    </li>
  );
};

export function DealScenarioPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/forecasting/scenarios/deals/${dealId}`);
      setData(res.data?.scenarios || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refetch(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  if (loading || !data || !data.active) return null;

  const current = data.current || {};
  const collapse = current.collapse || {};
  const conf = data.confidence_with_intervention || {};
  const noInt = data.confidence_no_intervention || {};
  const cmp = data.intervention_compare;
  const tl = data.timeline_forecast;
  const collapseTone = BAND_TONE[collapse.band] || BAND_TONE.Watching;

  return (
    <section className="gp-card overflow-hidden"
             data-testid="dealroom-scenario-panel">
      <header className="p-4 sm:p-5">
        <div className="flex items-center gap-1.5">
          <LineChart size={11} className="text-violet-600" strokeWidth={2.3} />
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
            Forecast & Scenarios
          </p>
        </div>
        <h2 className="text-[15px] sm:text-[16px] font-semibold text-slate-900 mt-1 leading-tight"
            data-testid="dealroom-scenario-narrative">
          {data.narrative}
        </h2>
        <div className="flex items-center flex-wrap gap-1.5 mt-2">
          <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                style={{ background: collapseTone.bg, color: collapseTone.fg }}
                data-testid="dealroom-scenario-current-band">
            {collapse.band}
          </span>
          <span className="text-[10.5px] tabular-nums px-2 py-0.5 rounded-md"
                style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
            Collapse {collapse.score}/100
          </span>
          {conf.projected_score ? (
            <span className="text-[10.5px] tabular-nums px-2 py-0.5 rounded-md"
                  style={{ background: "rgba(16,185,129,0.08)", color: "#047857" }}>
              With intervention · {conf.projected_score}/100 in {conf.points?.length - 1} wks
            </span>
          ) : null}
        </div>
      </header>
      {(collapse.contributions || []).length ? (
        <div className="px-4 sm:px-5 pb-2">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle size={11} strokeWidth={2.3} style={{ color: "#92400E" }} />
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
               style={{ color: "var(--gp-violet, #7C3AED)" }}>
              Contributing signals
            </p>
          </div>
          <ul data-testid="scenario-signals">
            {collapse.contributions.map((c) => <SignalRow key={c.signal} signal={c} />)}
          </ul>
        </div>
      ) : null}
      {(data.scenarios || []).length ? (
        <div className="px-4 sm:px-5 pb-3 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles size={11} strokeWidth={2.3} style={{ color: "var(--gp-violet, #7C3AED)" }} />
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
               style={{ color: "var(--gp-violet, #7C3AED)" }}>
              Forward scenarios
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              data-testid="scenario-list">
            {data.scenarios.map((s) => <ScenarioCard key={s.kind} s={s} />)}
          </ul>
        </div>
      ) : null}
      {cmp ? (
        <div className="px-4 sm:px-5 pb-3 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}
             data-testid="scenario-intervention-compare">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Hourglass size={11} strokeWidth={2.3} style={{ color: "var(--gp-violet, #7C3AED)" }} />
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
               style={{ color: "var(--gp-violet, #7C3AED)" }}>
              Intervene now vs delay
            </p>
          </div>
          <p className="text-[12px] text-slate-700 leading-snug">
            {cmp.narrative}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="rounded-md p-2"
                 style={{ background: "rgba(16,185,129,0.06)",
                          border: "1px solid rgba(16,185,129,0.20)" }}>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                Intervene now
              </p>
              <p className="text-[12px] font-bold text-emerald-800 mt-0.5 tabular-nums">
                +{cmp.intervene_now?.confidence_uplift_pts} confidence pts
              </p>
              <p className="text-[10px] text-slate-500">
                {fmtGBP(cmp.intervene_now?.fee_recovery_gbp)} fee recoverable
              </p>
            </div>
            <div className="rounded-md p-2"
                 style={{ background: "rgba(245,158,11,0.05)",
                          border: "1px solid rgba(245,158,11,0.18)" }}>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800">
                Delay {cmp.delay_days} days
              </p>
              <p className="text-[12px] font-bold text-amber-900 mt-0.5 tabular-nums">
                +{cmp.delay_path?.confidence_uplift_pts} confidence pts
              </p>
              <p className="text-[10px] text-slate-500">
                {fmtGBP(cmp.delay_path?.fee_recovery_gbp)} fee recoverable
              </p>
            </div>
          </div>
        </div>
      ) : null}
      {tl ? (
        <div className="px-4 sm:px-5 pb-4 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}
             data-testid="scenario-timeline-forecast">
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
             style={{ color: "var(--gp-violet, #7C3AED)" }}>
            Timeline forecast
          </p>
          <p className="text-[11.5px] text-slate-700 leading-snug mt-1">
            {tl.narrative}
          </p>
        </div>
      ) : null}
      {noInt?.points?.length ? (
        <div className="px-4 sm:px-5 pb-4 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}
             data-testid="scenario-confidence-trajectory">
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
             style={{ color: "var(--gp-violet, #7C3AED)" }}>
            Confidence trajectory (no intervention)
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-[18px] font-bold tabular-nums text-slate-900">
              {noInt.starting_score}
            </span>
            <span className="text-[10.5px] text-slate-500">→</span>
            <span className={`text-[18px] font-bold tabular-nums ${noInt.delta_pts < 0 ? "text-rose-700" : "text-emerald-700"}`}>
              {noInt.projected_score}
            </span>
            <span className="text-[10.5px] text-slate-500">
              ({noInt.delta_pts >= 0 ? "+" : ""}{noInt.delta_pts} pts in {noInt.points.length - 1} wks)
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default DealScenarioPanel;
